import { config } from "../config";
import axios from "axios";

export class UserService {
    isAuthenticated = false;
    user = null;
    authToken = null;

    constructor(httpClient = axios) {
        // dependency injection for testing
        this.httpClient = httpClient;
    }

    /**
     * retrieve the authentication token from local storage or cached state.
     */
    getAuthToken() {
        if (!this.authToken) {
            this.authToken = localStorage.getItem("token");
        }
        return this.authToken;
    }

    /**
     * login using username and password
     * @param {string} username
     * @param {string} password
     */
    async login(username, password) {
        return await this.wrapAPICall(async () => {
            const response = await this.httpPost(`${config.apiUrl}/users/login`, { username, password });
            if (response?.user?.authenticated) {
                return this.onLoginResponse(response.user);
            } else {
                alert("Invalid username/password combination.");
                return null;
            }
        }, "Login failed");
    }

    logout() {
        this.isAuthenticated = false;
        this.user = null;
        this.authToken = null;
        localStorage.clear();
    }

    /**
     * login using Google OAuth token
     * @param {string} token - oauth token
     */
    async loginGoogle(token) {
        return await this.wrapAPICall(async () => {
            const response = await this.httpPost(`${config.apiUrl}/users/googleLogin`, { token });
            return this.onLoginResponse(response);
        }, "Google login failed");
    }

    /**
     * process login response && update user state
     * @param {Object} user
     */
    onLoginResponse(user) {
        if (user?.authenticated) {
            this.user = user;
            this.isAuthenticated = true;
            this.authToken = user.token;
            return user;
        } else {
            throw new Error("User authentication failed.");
        }
    }

    /**
     * redirs the user to the appropriate portal URL
     */
    redirectToPortal() {
        const isProd = window.location.href.includes("prod");
        const portalUrl = isProd ? "https://portal.linevisioninc.com/" : "https://dev-portal.linevisioninc.com/";
        window.open(portalUrl, "_blank");
    }

    /**
     * get analytics data for the current user
     */
    async getAnalytics() {
        return await this.wrapAPICall(async () => {
            const token = this.getAuthToken();
            return await this.httpGet(`${config.apiUrl}/users/analytics`, token);
        }, "Failed to fetch analytics");
    }

    /**
     * get analytics data for user within date range.
     * @param {string} userId
     * @param {string} startDate
     * @param {string} endDate
     */
    async getUserAnalytics(userId, startDate, endDate) {
        return await this.wrapAPICall(async () => {
            const token = this.getAuthToken();
            return await this.httpGet(`${config.apiUrl}/users/analytics/${userId}`, token, {
                startDate,
                endDate,
            });
        }, "Failed to fetch user analytics");
    }

    /**
     * http helper for POST requests
     */
    async httpPost(url, body, sendJwt = false) {
        const headers = sendJwt ? this.getDefaultHeaders(this.getAuthToken()) : this.getDefaultHeaders();
        const response = await this.httpClient.post(url, body, { headers });
        return response.data;
    }

    /**
     * http helper for GET requests
     */
    async httpGet(url, token = null, params = {}) {
        const headers = this.getDefaultHeaders(token);
        const response = await this.httpClient.get(url, { headers, params });
        return response.data;
    }

    /**
     * default headers for HTTP requests
     */
    getDefaultHeaders(token = null) {
        const headers = {
            Accept: "application/json",
            "Content-Type": "application/json",
            "x-client-type": "portal",
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        return headers;
    }

    /**
     * wrapper to handle API calls and errors
     */
    async wrapAPICall(apiCall, errorMessage) {
        try {
            return await apiCall();
        } catch (error) {
            const detailedError = error?.response?.data?.message || error.message || "Unknown error";
            console.error(`${errorMessage}:`, detailedError);
            throw new Error(`${errorMessage}: ${detailedError}`);
        }
    }
}

export const userService = new UserService();
